import React, { useState, useEffect, KeyboardEvent, ReactNode } from 'react'
import './Pill.scss'

export type SelectablePillProps = {
    label: string | ReactNode;
    value?: any;
    selected?: boolean;
    defaultSelected?: boolean;
    disabled?: boolean;
    onChange?: (selected: boolean, value?: any) => void;
    removable?: boolean;
    onRemove?: (value?: any) => void;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
};

export default function Pill({
    label,
    value,
    selected,
    defaultSelected = false,
    disabled = false,
    onChange,
    removable = false,
    onRemove,
    className = '',
    size = 'md',
}: SelectablePillProps) {
    const [internalSelected, setInternalSelected] = useState<boolean>(defaultSelected);

    useEffect(() => {
        if (typeof selected === 'boolean') {
            setInternalSelected(selected);
        }
    }, [selected]);

    const isSelected = typeof selected === 'boolean' ? selected : internalSelected;

    function toggle() {
        if (disabled) return;
        const next = !isSelected;
        if (typeof selected !== 'boolean') setInternalSelected(next);
        onChange?.(next, value);
    }

    function handleKey(e: KeyboardEvent<HTMLButtonElement>) {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    }

    function handleRemove(e: React.MouseEvent) {
        e.stopPropagation();
        if (disabled) return;
        onRemove?.(value);
    }

    return (
        <button
            type="button"
            role="button"
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={toggle}
            onKeyDown={handleKey}
            className={`pill ${size} ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${className}`}>
            <span
                style={{ wordBreak: 'normal', textOverflow: 'clip', textWrap: 'nowrap', maxLines: 1, whiteSpace: 'nowrap' }}
                className="label">
                {label}
            </span>
            {removable && (
                <span className="remove" onClick={handleRemove} role="button" aria-label={`Remove ${label}`}>
                    &times;
                </span>
            )}
        </button>
    );
}
